import GroupInviteLinksService from 'services/group_invite_links';
import { displayErrorAlert, displaySuccessAlert } from 'services/alerts';

export const FETCH_GROUP_INVITE_LINKS_REQUESTED = 'FETCH_GROUP_INVITE_LINKS_REQUESTED';
export const FETCH_GROUP_INVITE_LINKS_SUCCEEDED = 'FETCH_GROUP_INVITE_LINKS_SUCCEEDED';
export const FETCH_GROUP_INVITE_LINKS_FAILED = 'FETCH_GROUP_INVITE_LINKS_FAILED';

export const CREATE_GROUP_INVITE_LINK_REQUESTED = 'CREATE_GROUP_INVITE_LINK_REQUESTED';
export const CREATE_GROUP_INVITE_LINK_SUCCEEDED = 'CREATE_GROUP_INVITE_LINK_SUCCEEDED';
export const CREATE_GROUP_INVITE_LINK_FAILED = 'CREATE_GROUP_INVITE_LINK_FAILED';

export const UPDATE_GROUP_INVITE_LINK_REQUESTED = 'UPDATE_GROUP_INVITE_LINK_REQUESTED';
export const UPDATE_GROUP_INVITE_LINK_SUCCEEDED = 'UPDATE_GROUP_INVITE_LINK_SUCCEEDED';
export const UPDATE_GROUP_INVITE_LINK_FAILED = 'UPDATE_GROUP_INVITE_LINK_FAILED';

export const DELETE_GROUP_INVITE_LINK_REQUESTED = 'DELETE_GROUP_INVITE_LINK_REQUESTED';
export const DELETE_GROUP_INVITE_LINK_SUCCEEDED = 'DELETE_GROUP_INVITE_LINK_SUCCEEDED';
export const DELETE_GROUP_INVITE_LINK_FAILED = 'DELETE_GROUP_INVITE_LINK_FAILED';

export const SHOW_GROUP_INVITE_DIALOG = 'SHOW_GROUP_INVITE_DIALOG';
export const HIDE_GROUP_INVITE_DIALOG = 'HIDE_GROUP_INVITE_DIALOG';

export const fetchGroupInviteLinks = (groupId) => async (dispatch) => {
    if (!groupId) {
        console.warn('Attempted to fetch group invite links without a group ID');
        return;
    }

    console.log('[Group Invite] Fetching invite links for group ID:', groupId);
    dispatch({ type: FETCH_GROUP_INVITE_LINKS_REQUESTED, payload: { groupId } });
    
    try {
        console.log('[Group Invite] Calling service with group ID:', groupId);
        const response = await GroupInviteLinksService.getGroupInviteLinks(groupId);
        console.log('[Group Invite] Full response:', response);
        console.log('[Group Invite] Response data:', response?.data);
        
        // Handle the response format properly with more detailed logging
        let inviteLinks = [];
        console.log('[Group Invite] Response type:', response && typeof response.data);
        
        if (response && response.data) {
            // If response.data is already an array, use it
            if (Array.isArray(response.data)) {
                inviteLinks = response.data;
                console.log('[Group Invite] Using direct array from response.data');
            }
            // Otherwise, if response.data contains a data property with the array, use that
            else if (response.data.data && Array.isArray(response.data.data)) {
                inviteLinks = response.data.data;
                console.log('[Group Invite] Using nested array from response.data.data');
            }
            // Handle case where a single object is returned
            else if (typeof response.data === 'object') {
                // If it's an object with an 'is_active' property, it's likely a single link
                if ('is_active' in response.data && 'group_id' in response.data) {
                    inviteLinks = [response.data];
                    console.log('[Group Invite] Using single object as array');
                }
                // Log all object keys to help debug
                console.log('[Group Invite] Response object keys:', Object.keys(response.data));
            }
        }
        
        console.log('[Group Invite] Parsed invite links:', inviteLinks);
        
        // Filter to only show active links (if needed later)
        const activeInviteLinks = inviteLinks.filter(link => link.is_active);
        console.log('[Group Invite] Active invite links:', activeInviteLinks.length);
        
        // Send both all invite links and current group ID in the payload
        dispatch({
            type: FETCH_GROUP_INVITE_LINKS_SUCCEEDED,
            payload: {
                inviteLinks: inviteLinks, 
                groupId: groupId
            }
        });
    } catch (error) {
        console.error('Failed to fetch group invite links:', error);
        dispatch({ type: FETCH_GROUP_INVITE_LINKS_FAILED });
    }
};

export const createGroupInviteLink = (data) => async (dispatch) => {
    // Validate required data
    if (!data || !data.group_id) {
        console.error('Cannot create invite link: Missing group_id');
        dispatch({ type: CREATE_GROUP_INVITE_LINK_FAILED });
        displayErrorAlert('Cannot create invite link: Missing group ID');
        return;
    }
    
    dispatch({ type: CREATE_GROUP_INVITE_LINK_REQUESTED });
    
    try {
        // Ensure data is properly formatted and remove any stale invite_code
        const preparedData = {
            ...data,
            // Only include invite_code if explicitly provided and not empty
            invite_code: data.invite_code && data.invite_code.trim() !== '' ? data.invite_code.trim() : undefined,
            // Handle max_uses - if empty string or 0, set to null (unlimited)
            max_uses: data.max_uses === '' || data.max_uses === undefined || data.max_uses === null 
                ? null 
                : (parseInt(data.max_uses, 10) || null),
            // Handle expires_in_days - if not provided or null or empty string, set to null (no expiration)
            expires_in_days: data.expires_in_days !== undefined && data.expires_in_days !== null && data.expires_in_days !== '' 
                ? data.expires_in_days 
                : null
        };
        
        // Log the prepared data to ensure invite_code is being handled correctly
        console.log('[Action] Prepared invite link data:', {
            ...preparedData,
            // Don't log any passwords for security
            passcode: preparedData.passcode ? '[REDACTED]' : null
        });
        
        console.debug('Sending invite link data:', preparedData);
        const response = await GroupInviteLinksService.createGroupInviteLink(preparedData);
        
        // Handle both formats: direct response or response with data property
        const responseData = response.data || response;
        
        if (!responseData) {
            console.error('Empty response received:', response);
            throw new Error('Empty response received');
        }
        
        console.log('Received response:', responseData);
        
        dispatch({
            type: CREATE_GROUP_INVITE_LINK_SUCCEEDED,
            payload: responseData
        });
        
        // Dispatch a custom event to notify other components
        window.dispatchEvent(new CustomEvent('inviteLinkCreated', { detail: responseData }));
        
        displaySuccessAlert('Group invite link created successfully');
    } catch (error) {
        console.error('Failed to create group invite link:', error);
        dispatch({ type: CREATE_GROUP_INVITE_LINK_FAILED });
        
        // Extract the error message from the response if available
        const errorMessage = error.response?.data?.statusMessage || error.message || 'Failed to create group invite link';
        displayErrorAlert(errorMessage);
    }
};

export const updateGroupInviteLink = (inviteCode, data) => async (dispatch) => {
    console.log('[Group Update] Starting update of invite link with code:', inviteCode);
    console.log('[Group Update] Update data:', data);
    dispatch({ type: UPDATE_GROUP_INVITE_LINK_REQUESTED });
    
    try {
        // Prepare the data to ensure max_uses is correctly handled
        const preparedData = {
            ...data,
            // Handle max_uses - if empty string, set to null (unlimited)
            max_uses: data.max_uses === '' || data.max_uses === undefined ? null : (parseInt(data.max_uses, 10) || null),
            // Handle expires_in_days - if not provided or null or empty string, set to null (no expiration)
            expires_in_days: data.expires_in_days !== undefined && data.expires_in_days !== null && data.expires_in_days !== '' 
                ? data.expires_in_days 
                : null
        };
        
        console.log('[Group Update] Prepared data for API:', preparedData);
        console.log('[Group Update] Calling API service with invite code:', inviteCode);
        
        const response = await GroupInviteLinksService.updateGroupInviteLink(inviteCode, preparedData);
        console.log('[Group Update] API response:', response);
        
        // Handle response format: the API might return the data directly or wrapped in a data property
        const responseData = response.data || response;
        console.log('[Group Update] Processed response data:', responseData);
        
        // Verify we have a valid response with required fields
        if (!responseData || !responseData.invite_code) {
            console.error('[Group Update] Invalid response data:', responseData);
            throw new Error('Invalid response format from API');
        }
        
        dispatch({
            type: UPDATE_GROUP_INVITE_LINK_SUCCEEDED,
            payload: responseData
        });
        displaySuccessAlert('Group invite link updated successfully');
        
        // Re-fetch invite links to ensure UI is up to date
        if (data.group_id) {
            console.log('[Group Update] Re-fetching invite links for group:', data.group_id);
            dispatch(fetchGroupInviteLinks(data.group_id));
        }
    } catch (error) {
        console.error('[Group Update] Error updating invite link:', error);
        console.error('[Group Update] Error details:', {
            message: error.message,
            stack: error.stack,
            response: error.response,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
        });
        
        dispatch({ type: UPDATE_GROUP_INVITE_LINK_FAILED });
        
        // Extract the error message from the response if available
        const errorMessage = error.response?.data?.statusMessage || error.message || 'Failed to update group invite link';
        displayErrorAlert(errorMessage);
    }
};

export const deleteGroupInviteLink = (inviteLink) => async (dispatch) => {
    if (!inviteLink || !inviteLink.invite_code) {
        console.error('Cannot deactivate link: Missing invite_code');
        dispatch({ type: DELETE_GROUP_INVITE_LINK_FAILED });
        displayErrorAlert('Cannot deactivate invite link: Missing code');
        return;
    }
    
    dispatch({ type: DELETE_GROUP_INVITE_LINK_REQUESTED });
    
    try {
        // Instead of deleting, we just deactivate the link by setting is_active to false
        await GroupInviteLinksService.deactivateGroupInviteLink(inviteLink.invite_code);
        
        dispatch({
            type: DELETE_GROUP_INVITE_LINK_SUCCEEDED,
            payload: { id: inviteLink.id }
        });
        
        // Dispatch a custom event to notify other components
        window.dispatchEvent(new CustomEvent('inviteLinkDeleted', { detail: inviteLink }));
        
        displaySuccessAlert('Group invite link has been deactivated');
    } catch (error) {
        console.error('Failed to deactivate group invite link:', error);
        dispatch({ type: DELETE_GROUP_INVITE_LINK_FAILED });
        
        displayErrorAlert('Failed to deactivate group invite link');
    }
};

export const showGroupInviteDialog = (groupId, teamId = null, teamName = null, groupName = null) => ({
    type: SHOW_GROUP_INVITE_DIALOG,
    payload: { groupId, teamId, teamName, groupName }
});

export const hideGroupInviteDialog = () => ({
    type: HIDE_GROUP_INVITE_DIALOG
});
